import { useMemo, useState, type FormEvent } from 'react'
import { useAppStore } from '@/store/app'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, Copy, KeyRound, Link2, Trash2 } from 'lucide-react'
import { applicationsApi, inviteLinkFor, PRIVILEGED_INTENTS, type ApplicationPatch } from '@/lib/applicationsApi'
import { describeError } from '@/lib/auth'
import { P, PERMISSION_GROUPS, type PermissionKey } from '@/lib/permissions'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import { cx } from '@/lib/cx'
import type { Application } from '@/types/api'
import { Field } from '@/pages/AuthPage'
import auth from '@/pages/AuthPage.module.css'
import layer from './SettingsLayer.module.css'
import gs from '../guild/GuildSettingsLayer.module.css'
import styles from './ApplicationsSection.module.css'

const AGREEMENT_URL = '/legal/developer-agreement'

/** Настройки → «Боты»: приложения разработчика — создание, токен, intents, ссылка для добавления на сервер. */
export function ApplicationsSection() {
  const qc = useQueryClient()
  const apps = useQuery({ queryKey: ['applications'], queryFn: applicationsApi.list })
  const [selected, setSelected] = useState<string | null>(null)
  const [freshToken, setFreshToken] = useState<{ appId: string; token: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['applications'] })

  const create = useMutation({
    mutationFn: (v: { name: string; description: string }) => applicationsApi.create(v.name, v.description),
    onSuccess: (r) => {
      setFreshToken({ appId: r.application.id, token: r.token })
      setSelected(r.application.id)
      refresh()
    },
    onError: (err) => {
      const d = describeError(err)
      setError(d.fields.name ?? d.fields.developer_agreement ?? d.message)
    },
  })

  const list = apps.data ?? []
  const devMode = useAppStore((s) => s.devMode)
  const setDevMode = useAppStore((s) => s.setDevMode)
  const current = list.find((a) => a.id === selected) ?? null

  if (current) {
    return (
      <ApplicationDetail
        app={current}
        freshToken={freshToken?.appId === current.id ? freshToken.token : null}
        onToken={(token) => setFreshToken({ appId: current.id, token })}
        onBack={() => { setSelected(null); setFreshToken(null) }}
        onChanged={refresh}
        onDeleted={() => { setSelected(null); setFreshToken(null); refresh() }}
      />
    )
  }

  return (
    <section>
      <h1 className={layer.sectionTitle}>Боты</h1>
      <p className={layer.text}>Бот — приложение с собственным аккаунтом.</p>
      <ul className={layer.points}>
        <li>discord.js и discord.py работают после смены адреса API, шлюза и токена: те же события, права и intents</li>
        <li>на сервер бот попадает только по ссылке авторизации</li>
        <li>за бота отвечает владелец приложения</li>
      </ul>
      <h2 className={layer.subTitle}>Режим разработчика</h2>
      <label className={styles.devToggle}>
        <input type="checkbox" checked={devMode} onChange={(e) => setDevMode(e.target.checked)} />
        <span className={styles.devText}>
          <span className={styles.devLabel}>Показывать «Скопировать ID»</span>
          <span className={styles.devHint}>Пункт появится в меню по правой кнопке. Нужен, когда настраиваете ботов или разбираетесь с обращением в поддержку; остальным только мешает</span>
        </span>
      </label>

      <h2 className={layer.subTitle}>Мои приложения</h2>
      <CreateForm busy={create.isPending} error={error} onSubmit={(name, description) => { setError(null); create.mutate({ name, description }) }} />
      {apps.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {!apps.isPending && list.length === 0 ? <p className={layer.text}>Приложений пока нет.</p> : null}
      <ul className={styles.list}>
        {list.map((a) => (
          <li key={a.id}>
            <button type="button" className={styles.appRow} onClick={() => setSelected(a.id)}>
              <span className={styles.appIcon} aria-hidden="true"><Bot size={20} /></span>
              <span className={styles.appMain}>
                <span className={styles.appName}>{a.name}</span>
                <span className={styles.appMeta}>
                  {a.bot ? `@${a.bot.username}` : 'без бота'} · {a.approximate_guild_count ?? 0} {plural(a.approximate_guild_count ?? 0, 'сервер', 'сервера', 'серверов')} · {a.bot_public ? 'публичный' : 'приватный'}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

function CreateForm({ busy, error, onSubmit }: { busy: boolean; error: string | null; onSubmit: (name: string, description: string) => void }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [agree, setAgree] = useState(false)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!agree || name.trim().length < 2) return
    onSubmit(name.trim(), description.trim())
    setName('')
    setDescription('')
  }
  return (
    <form className={styles.createForm} onSubmit={submit} aria-label="Новое приложение">
      <Field label="Название приложения" htmlFor="app-name" hint="От 2 до 32 символов; так же будет называться бот">
        <input id="app-name" className={auth.input} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} placeholder="Например, Дежурный" />
      </Field>
      <Field label="Описание" htmlFor="app-desc" hint="Показывается на странице добавления бота">
        <input id="app-desc" className={auth.input} value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)} placeholder="Что умеет бот" />
      </Field>
      <label className={styles.agree}>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
        <span>
          Принимаю <a href={AGREEMENT_URL} target="_blank" rel="noreferrer">соглашение разработчика</a>: отвечаю за бота, обрабатываю переписку и данные участников только для его работы и размещаю его в соответствии с законом о персональных данных.
        </span>
      </label>
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      <div className={layer.buttonRow}>
        <button type="submit" className={layer.button} disabled={busy || !agree || name.trim().length < 2}>{busy ? 'Создаём…' : 'Создать приложение'}</button>
      </div>
    </form>
  )
}

function ApplicationDetail({ app, freshToken, onToken, onBack, onChanged, onDeleted }: { app: Application; freshToken: string | null; onToken: (t: string) => void; onBack: () => void; onChanged: () => void; onDeleted: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const [name, setName] = useState(app.name)
  const [description, setDescription] = useState(app.description)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [perms, setPerms] = useState<bigint>(0n)
  const [copied, setCopied] = useState<string | null>(null)
  const guilds = useQuery({ queryKey: ['application-guilds', app.id], queryFn: () => applicationsApi.guilds(app.id) })

  const patch = useMutation({
    mutationFn: (p: ApplicationPatch) => applicationsApi.update(app.id, p),
    onSuccess: onChanged,
    onError: (err) => { const d = describeError(err); setError(d.fields.name ?? d.fields.description ?? d.fields.privileged_intents ?? d.message) },
  })
  const reset = useMutation({
    mutationFn: () => applicationsApi.resetToken(app.id),
    onSuccess: (r) => onToken(r.token),
    onError: (err) => setError(describeError(err).message),
  })
  const [secret, setSecret] = useState<string | null>(null)
  const resetSecret = useMutation({
    mutationFn: () => applicationsApi.resetSecret(app.id),
    onSuccess: (r) => setSecret(r.client_secret),
    onError: (err) => setError(describeError(err).message),
  })
  const remove = useMutation({
    mutationFn: () => applicationsApi.remove(app.id),
    onSuccess: onDeleted,
    onError: (err) => setError(describeError(err).message),
  })

  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setError('Не удалось скопировать: разрешите доступ к буферу обмена')
    }
  }
  const inviteLink = useMemo(() => inviteLinkFor(app.id, perms), [app.id, perms])
  const togglePerm = (key: PermissionKey) => setPerms((v) => (v & P[key] ? v & ~P[key] : v | P[key]))
  const gatewayHint = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/gateway`

  return (
    <section>
      <button type="button" className={styles.back} onClick={onBack}>← Все приложения</button>
      <h1 className={layer.sectionTitle}>{app.name}</h1>
      {error ? <p className={gs.error} role="alert">{error}</p> : null}

      <div className={styles.idRow}>
        <span className={styles.idLabel}>ID приложения</span>
        <code className={gs.code}>{app.id}</code>
        <button type="button" className={gs.smallButton} onClick={() => void copy(app.id, 'id')} aria-label="Скопировать id приложения"><Copy size={14} /> {copied === 'id' ? 'Скопировано' : 'Копировать'}</button>
      </div>
      {app.bot ? <p className={layer.text}>Бот: <strong>@{app.bot.username}</strong> (id {app.bot.id}). Отображаемое имя бота совпадает с названием приложения.</p> : null}

      <h2 className={layer.subTitle}>Основное</h2>
      <Field label="Название" htmlFor="app-edit-name">
        <input id="app-edit-name" className={auth.input} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} onBlur={() => { if (name.trim() !== app.name && name.trim().length >= 2) patch.mutate({ name: name.trim() }) }} />
      </Field>
      <Field label="Описание" htmlFor="app-edit-desc">
        <textarea id="app-edit-desc" className={cx(auth.input, styles.textarea)} value={description} maxLength={400} rows={3} onChange={(e) => setDescription(e.target.value)} onBlur={() => { if (description.trim() !== app.description) patch.mutate({ description: description.trim() }) }} />
      </Field>
      <label className={styles.toggle}>
        <input type="checkbox" checked={app.bot_public} onChange={(e) => patch.mutate({ bot_public: e.target.checked })} />
        <span className={styles.toggleText}>
          <span className={styles.toggleLabel}>Публичный бот</span>
          <span className={styles.toggleHint}>Добавлять бота на серверы может любой участник с правом «Управлять сервером»; иначе — только вы.</span>
        </span>
      </label>

      <h2 className={layer.subTitle}>Привилегированные intents</h2>
      <p className={layer.text}>Без них шлюз закрывает сессию бота кодом 4014, если библиотека запросила эти intents. В discord.py это ошибка PrivilegedIntentsRequired.</p>
      {PRIVILEGED_INTENTS.map((i) => (
        <label key={i.bit} className={styles.toggle}>
          <input type="checkbox" checked={(app.privileged_intents & i.bit) !== 0} onChange={(e) => patch.mutate({ privileged_intents: e.target.checked ? app.privileged_intents | i.bit : app.privileged_intents & ~i.bit })} />
          <span className={styles.toggleText}>
            <span className={styles.toggleLabel}>{i.label}</span>
            <span className={styles.toggleHint}>{i.hint}</span>
          </span>
        </label>
      ))}

      <h2 className={layer.subTitle}>Токен бота</h2>
      {freshToken ? (
        <div className={styles.tokenBox} role="status">
          <p className={styles.tokenWarn}>Скопируйте токен сейчас — больше он не показывается. Тот, у кого есть токен, управляет ботом.</p>
          <code className={styles.token}>{freshToken}</code>
          <button type="button" className={gs.smallButton} onClick={() => void copy(freshToken, 'token')}><Copy size={14} /> {copied === 'token' ? 'Скопировано' : 'Копировать токен'}</button>
        </div>
      ) : (
        <p className={layer.text}>Токен показывается один раз при создании и при перевыпуске. Перевыпуск отзывает старый токен и закрывает сессии бота кодом 4004.</p>
      )}
      <div className={layer.buttonRow}>
        <button type="button" className={gs.smallButton} disabled={reset.isPending} onClick={() => reset.mutate()}><KeyRound size={14} /> {reset.isPending ? 'Перевыпускаем…' : 'Перевыпустить токен'}</button>
      </div>

      <h2 className={layer.subTitle}>Вход через Кличат (OAuth2)</h2>
      <p className={layer.text}>Приложение может входить за пользователя:</p>
      <ol className={layer.steps}>
        <li>ссылка авторизации: <code className={gs.code}>{location.origin}/oauth2/authorize?client_id={app.id}&amp;response_type=code&amp;scope=identify&amp;redirect_uri=…</code></li>
        <li>обмен кода на токен: <code className={gs.code}>POST /api/v1/oauth2/token</code>; мобильным и настольным клиентам — с PKCE (S256), серверным — с client_secret</li>
      </ol>
      <p className={layer.text}>Адрес возврата должен точно совпадать с одним из указанных выше; http — только для localhost.</p>
      {secret ? (
        <div className={styles.tokenBox}>
          <code className={styles.token}>{secret}</code>
          <button type="button" className={gs.smallButton} onClick={() => void copy(secret, 'secret')}><Copy size={14} /> {copied === 'secret' ? 'Скопировано' : 'Копировать секрет'}</button>
        </div>
      ) : (
        <p className={layer.text}>Секрет показывается один раз. Сброс закрывает доступ прежним серверным клиентам.</p>
      )}
      <div className={layer.buttonRow}>
        <button type="button" className={gs.smallButton} disabled={resetSecret.isPending} onClick={() => resetSecret.mutate()}><KeyRound size={14} /> {resetSecret.isPending ? 'Сбрасываем…' : 'Сбросить client_secret'}</button>
      </div>
      <p className={layer.text}>Подключение библиотек:</p>
      <ul className={layer.points}>
        <li>REST: <code className={gs.code}>{location.origin}/api</code>, пути /api/v9 и /api/v10 — псевдонимы /api/v1</li>
        <li>шлюз: <code className={gs.code}>{gatewayHint}</code></li>
        <li>заголовок: <code className={gs.code}>Authorization: Bot &lt;токен&gt;</code></li>
      </ul>

      <h2 className={layer.subTitle}>Ссылка для добавления на сервер</h2>
      <p className={layer.text}>Отметьте права, которые нужны боту: на сервере он получит роль с этими правами, урезанными до прав того, кто его добавляет.</p>
      <div className={styles.permGroups}>
        {PERMISSION_GROUPS.map((g) => (
          <fieldset key={g.title} className={styles.permGroup}>
            <legend className={styles.permTitle}>{g.title}</legend>
            {g.items.map((item) => (
              <label key={item.key} className={cx(styles.perm, item.key === 'ADMINISTRATOR' && styles.permAdmin)}>
                <input type="checkbox" checked={(perms & P[item.key]) !== 0n} onChange={() => togglePerm(item.key)} />
                <span>{item.label}</span>
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      <div className={styles.linkRow}>
        <Link2 size={16} />
        <code className={styles.link}>{inviteLink}</code>
        <button type="button" className={gs.smallButton} onClick={() => void copy(inviteLink, 'link')}><Copy size={14} /> {copied === 'link' ? 'Скопировано' : 'Копировать'}</button>
      </div>

      <h2 className={layer.subTitle}>Серверы бота</h2>
      {guilds.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {guilds.data && guilds.data.length === 0 ? <p className={layer.text}>Бот пока не добавлен ни на один сервер.</p> : null}
      <ul className={styles.guilds}>
        {(guilds.data ?? []).map((g) => (
          <li key={g.id} className={styles.guild}>
            <span className={styles.guildIcon} style={{ background: guildColor(g.id) }} aria-hidden="true">{guildInitials(g.name)}</span>
            <span>{g.name}</span>
          </li>
        ))}
      </ul>

      <div className={layer.divider} />
      <h2 className={layer.subTitle}>Опасная зона</h2>
      <p className={layer.text}>Удаление приложения выводит бота со всех серверов, отзывает токен и гасит его аккаунт. Отменить нельзя.</p>
      <div className={layer.buttonRow}>
        {confirmDelete ? (
          <>
            <button type="button" className={gs.smallDanger} disabled={remove.isPending} onClick={() => remove.mutate()}><Trash2 size={14} /> {remove.isPending ? 'Удаляем…' : 'Да, удалить приложение'}</button>
            <button type="button" className={gs.smallButton} onClick={() => setConfirmDelete(false)}>Отмена</button>
          </>
        ) : (
          <button type="button" className={gs.smallDanger} onClick={() => setConfirmDelete(true)}><Trash2 size={14} /> Удалить приложение</button>
        )}
      </div>
    </section>
  )
}
